import { Button, IconButton, Slider } from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import ReplayIcon from "@material-ui/icons/Replay";
import React, { useRef, useEffect, useState } from "react";
import api from "../../services/api";
import { Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const LS_NAME = "audioMessageRate";

// Player de voz no padrão do WhatsApp Web. Tudo herda a cor do texto de onde está (bolha
// recebida ou enviada, claro ou escuro, palco da prévia de envio), por isso não há cor de
// tema aqui: só `inherit`, cinzas translúcidos e o azul de "ouvido" do WhatsApp na trilha.
const WA_TRACK = "#53bdeb";

const useStyles = makeStyles((theme) => ({
  audioContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 340,
    minWidth: 240,
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
    border: 'none',
    color: 'inherit',
    whiteSpace: 'normal'
  },
  controlsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  // Player próprio (sem `controls` nativo): o painel nativo do Safari colapsa em larguras
  // pequenas e o botão da esquerda vira o menu "Velocidade de Reprodução" — o atendente
  // clicava para ouvir e caía nesse menu (15/09/2026). Aqui cada botão tem uma função só.
  playerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    minHeight: 44
  },
  playButton: {
    flex: 'none',
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: '50%',
    color: 'inherit',
    opacity: 0.72,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'rgba(127,127,127,0.18)', opacity: 1 },
    '& svg': { fontSize: 30 }
  },
  progressBox: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '0 6px 0 2px'
  },
  slider: {
    height: 3,
    padding: '9px 0 7px',
    color: WA_TRACK,
    '& .MuiSlider-rail': { height: 3, borderRadius: 2, color: 'rgba(127,127,127,0.55)', opacity: 1 },
    '& .MuiSlider-track': { height: 3, borderRadius: 2 },
    '& .MuiSlider-thumb': {
      width: 12,
      height: 12,
      marginTop: -4.5,
      marginLeft: -6,
      boxShadow: 'none',
      '&:hover, &.Mui-focusVisible, &.MuiSlider-active': { boxShadow: '0 0 0 6px rgba(83,189,235,0.2)' }
    }
  },
  timeLabel: {
    fontSize: 11,
    lineHeight: '14px',
    color: 'inherit',
    opacity: 0.62,
    fontVariantNumeric: 'tabular-nums'
  },
  rateChip: {
    flex: 'none',
    fontSize: 12,
    fontWeight: 700,
    minWidth: 40,
    minHeight: 24,
    height: 24,
    padding: '0 8px',
    lineHeight: 1,
    borderRadius: 12,
    color: 'inherit',
    border: 0,
    backgroundColor: 'rgba(127,127,127,0.2)',
    '&:hover': { backgroundColor: 'rgba(127,127,127,0.32)' }
  },
  errorLine: {
    fontSize: 11.5,
    lineHeight: '14px',
    color: theme.palette.type === 'dark' ? '#f58a84' : theme.palette.error.main
  },
  transcriptionContainer: {
    width: '100%',
    marginTop: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  transcriptionText: {
    fontSize: 13,
    lineHeight: '18px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    padding: '7px 9px',
    color: 'inherit',
    backgroundColor: 'rgba(127,127,127,0.14)',
    borderRadius: 6,
    width: '100%',
    boxSizing: 'border-box'
  },
  transcribeButton: {
    fontSize: 12,
    fontWeight: 600,
    padding: '0 10px',
    minWidth: 0,
    minHeight: 26,
    height: 26,
    borderRadius: 13,
    color: 'inherit',
    border: 0,
    boxShadow: 'none',
    backgroundColor: 'rgba(127,127,127,0.16)',
    '&:hover': { backgroundColor: 'rgba(127,127,127,0.28)', boxShadow: 'none', border: 0 },
    '&.Mui-disabled': { color: 'inherit', opacity: 0.55, backgroundColor: 'rgba(127,127,127,0.12)', border: 0 }
  }
}));

const RATES = [1, 1.5, 2, 0.5];
const RETRY_DELAYS = [300, 1500, 4000];

const formatTime = (secs) => {
  if (!isFinite(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
};

const AudioModal = ({ url, message, disableTranscription = false }) => {
  const classes = useStyles();
  const audioRef = useRef(null);
  const attemptsRef = useRef(0);
  const retryTimerRef = useRef(null);
  const [audioRate, setAudioRate] = useState(
    parseFloat(localStorage.getItem(LS_NAME) || "1")
  );
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [transcription, setTranscription] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const body = message?.body ?? "";
  const transcrito = message?.transcrito ?? false;

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = audioRate;
    }
    localStorage.setItem(LS_NAME, audioRate);
  }, [audioRate]);

  // Uma falha momentânea (arquivo ainda não servido, rede, deploy) deixava o elemento
  // "morto": <source> só é escolhida na primeira carga, então sem load() o play nunca
  // mais anda. Recarrega até 3x com espera crescente; depois mostra "tentar de novo".
  const recover = (why) => {
    const el = audioRef.current;
    if (!el) return;
    if (attemptsRef.current >= RETRY_DELAYS.length) {
      setFailed(true);
      setPlaying(false);
      return;
    }
    const delay = RETRY_DELAYS[attemptsRef.current];
    attemptsRef.current += 1;
    console.warn("[AudioModal] recarregando mídia:", why, {
      tentativa: attemptsRef.current,
      src: el.currentSrc,
      readyState: el.readyState,
      networkState: el.networkState,
      error: el.error && el.error.code,
    });
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      try {
        el.load();
        el.playbackRate = audioRate;
        const p = el.play();
        if (p && p.catch) p.catch(() => {});
      } catch (e) {
        // sem ação: o usuário ainda pode clicar de novo
      }
    }, delay);
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    attemptsRef.current = 0;
    setFailed(false);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(isFinite(el.duration) ? el.duration : 0);
    el.playbackRate = audioRate;

    let stallTimer = null;
    const onPlaying = () => { attemptsRef.current = 0; setFailed(false); setPlaying(true); };
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setCurrentTime(0); };
    const onTime = () => { if (!seeking) setCurrentTime(el.currentTime || 0); };
    const onMeta = () => { if (isFinite(el.duration)) setDuration(el.duration); };
    const onPlay = () => {
      // NETWORK_NO_SOURCE (3): a primeira carga falhou; sem load() o play nunca vai andar.
      if (el.networkState === 3 || (el.readyState === 0 && el.error)) {
        recover("sem fonte ao dar play");
        return;
      }
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        if (!el.paused && el.currentTime === 0 && el.readyState < 3) recover("travou após o play");
      }, 1500);
    };
    const onError = () => {
      const code = el.error && el.error.code;
      console.warn("[AudioModal] erro de mídia", { src: el.currentSrc, code });
      recover("erro " + code);
    };
    const onStalled = () => { if (!el.paused && el.readyState < 3) recover("rede parada"); };

    el.addEventListener("playing", onPlaying);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    el.addEventListener("play", onPlay);
    el.addEventListener("error", onError);
    el.addEventListener("stalled", onStalled);
    // A URL mudou (mensagem atualizada): as <source> novas só valem após load().
    try { el.load(); } catch (e) { /* ignora */ }
    return () => {
      clearTimeout(stallTimer);
      clearTimeout(retryTimerRef.current);
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("error", onError);
      el.removeEventListener("stalled", onStalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (failed) {
      attemptsRef.current = 0;
      setFailed(false);
      recover("tentar de novo");
      return;
    }
    if (el.paused) {
      el.playbackRate = audioRate;
      const p = el.play();
      if (p && p.catch) p.catch((err) => {
        console.warn("[AudioModal] play recusado:", err && err.message);
        recover("play recusado");
      });
    } else {
      el.pause();
    }
  };

  const toggleRate = () => {
    const idx = RATES.indexOf(audioRate);
    setAudioRate(RATES[(idx + 1) % RATES.length]);
  };

  const handleSeek = (_e, value) => {
    setSeeking(true);
    setCurrentTime(value);
  };
  const handleSeekCommit = (_e, value) => {
    const el = audioRef.current;
    setSeeking(false);
    if (el && isFinite(value)) el.currentTime = value;
  };

  // O backend grava todo áudio em .ogg (Opus) e também em .mp3. Safari (Mac, iPad e iPhone) não
  // decodifica Ogg/Opus, e o iPad se apresenta como "Macintosh" na UA — por isso não dá para
  // decidir por user-agent. O .mp3 toca em todo navegador, então vai primeiro; o .ogg fica como
  // reserva (o navegador passa para a próxima <source> se a primeira falhar, ex.: áudio antigo
  // sem .mp3 gerado).
  const getAudioSource = () => {
    if (!url) return null;
    if (!/\.ogg(\?|#|$)/i.test(url)) {
      return <source src={url} />;
    }
    const mp3Url = url.replace(/\.ogg(\?|#|$)/i, ".mp3$1");
    return (
      <>
        <source src={mp3Url} type="audio/mpeg" />
        <source src={url} type="audio/ogg; codecs=opus" />
      </>
    );
  };

  const handleTranscribe = async () => {
    setIsTranscribing(true);
    try {
      let audioData = {
        wid: message.wid,
      };

      const { data } = await api.post(`/message/transcribeAudio`, audioData);

      if (data) {
        setTranscription(data);
      } else {
        console.error("Invalid response data:", data);
      }
    } catch (error) {
      console.error("Erro ao transcrever áudio:", error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const total = duration > 0 ? duration : 0;

  return (
    <div className={classes.audioContainer}>
      <audio ref={audioRef} preload="metadata" style={{ display: "none" }}>
        {getAudioSource()}
      </audio>

      <div className={classes.playerRow}>
        <IconButton
          className={classes.playButton}
          onClick={togglePlay}
          size="small"
          aria-label={failed ? "Tentar de novo" : playing ? "Pausar" : "Reproduzir"}
          title={failed ? "Tentar de novo" : playing ? "Pausar" : "Reproduzir"}
        >
          {failed ? <ReplayIcon /> : playing ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <div className={classes.progressBox}>
          <Slider
            className={classes.slider}
            value={Math.min(currentTime, total || currentTime)}
            min={0}
            max={total || 1}
            step={0.1}
            onChange={handleSeek}
            onChangeCommitted={handleSeekCommit}
            aria-label="Posição do áudio"
          />
          {failed ? (
            <span className={classes.errorLine}>Não foi possível carregar o áudio. Clique para tentar de novo.</span>
          ) : (
            <span className={classes.timeLabel}>
              {formatTime(currentTime)} / {formatTime(total)}
            </span>
          )}
        </div>

        <Button
          className={classes.rateChip}
          onClick={toggleRate}
          size="small"
          disableRipple
          title="Velocidade de reprodução"
          aria-label="Velocidade de reprodução"
        >
          {audioRate}x
        </Button>
      </div>

      <div className={classes.controlsContainer}>
        {!disableTranscription && (
          <div className={classes.transcriptionContainer}>
            {!transcrito ? (
              transcription ? (
                <Typography className={classes.transcriptionText} variant="body2">
                  <strong>Transcrição:</strong> {transcription}
                </Typography>
              ) : (
                <Button
                  onClick={handleTranscribe}
                  className={classes.transcribeButton}
                  disabled={isTranscribing}
                >
                  {isTranscribing ? "Transcrevendo..." : "Transcrever"}
                </Button>
              )
            ) : (
              <Typography className={classes.transcriptionText} variant="body2">
                <strong>Transcrição:</strong> {body}
              </Typography>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioModal;